import { ArrowUp, ArrowDown, Minus } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import type { PartyResultComparison } from '@/types'

interface ElectionComparisonProps {
  comparison: PartyResultComparison[] | undefined
  hasPreviousElection: boolean
  isLoading: boolean
  className?: string
}

export function ElectionComparison({
  comparison,
  hasPreviousElection,
  isLoading,
  className,
}: ElectionComparisonProps) {
  if (isLoading) {
    return (
      <Card className={cn(className)}>
        <CardHeader>
          <div className="bg-muted h-5 w-48 animate-pulse rounded" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-muted h-4 w-64 animate-pulse rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!hasPreviousElection) {
    return (
      <Card className={cn(className)}>
        <CardHeader>
          <CardTitle className="text-base">Historical Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">
            First election — no comparison data available.
          </p>
        </CardContent>
      </Card>
    )
  }

  if (!comparison || comparison.length === 0) {
    return null
  }

  const sorted = [...comparison].sort((a, b) => b.current_seats - a.current_seats)

  return (
    <Card className={cn(className)}>
      <CardHeader>
        <CardTitle className="text-base">Historical Comparison</CardTitle>
        <CardDescription>Changes compared to previous election</CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Party</TableHead>
              <TableHead className="text-center">Seats</TableHead>
              <TableHead className="text-center">Change</TableHead>
              <TableHead className="text-center">Vote %</TableHead>
              <TableHead className="text-center">Swing</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((party) => (
              <TableRow key={party.party_id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div
                      className="h-3 w-3 rounded-sm"
                      style={{ backgroundColor: party.party_color }}
                    />
                    <span className="font-medium">{party.party_name}</span>
                  </div>
                </TableCell>
                <TableCell className="text-center tabular-nums">
                  {party.current_seats}
                </TableCell>
                <TableCell className="text-center">
                  <ChangeIndicator value={party.seat_change} suffix="" />
                </TableCell>
                <TableCell className="text-center tabular-nums">
                  {party.current_share.toFixed(1)}%
                </TableCell>
                <TableCell className="text-center">
                  <ChangeIndicator value={party.vote_change} suffix="%" decimals={1} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

function ChangeIndicator({
  value,
  suffix,
  decimals = 0,
}: {
  value: number
  suffix: string
  decimals?: number
}) {
  if (Math.abs(value) < 0.05) {
    return (
      <span className="inline-flex items-center gap-0.5 text-muted-foreground text-sm">
        <Minus className="h-3 w-3" />
        0{suffix}
      </span>
    )
  }

  const formatted = decimals > 0 ? Math.abs(value).toFixed(decimals) : Math.abs(value)

  if (value > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-green-600 text-sm tabular-nums">
        <ArrowUp className="h-3 w-3" />
        +{formatted}{suffix}
      </span>
    )
  }

  return (
    <span className="inline-flex items-center gap-0.5 text-red-600 text-sm tabular-nums">
      <ArrowDown className="h-3 w-3" />
      -{formatted}{suffix}
    </span>
  )
}
